import { useOutsideClick } from 'hooks/useOutsideClick/useOutsideClick'
import {
	type FocusEvent,
	type FocusEventHandler,
	type KeyboardEvent,
	type ReactElement,
	type ReactNode,
	type RefObject,
	useCallback,
	useEffect,
} from 'react'
import { cx } from 'utils/cx'

export interface WrapperDropdownProps {
	readonly children: ReactElement
	readonly id: string
	readonly refDropdown: RefObject<HTMLDivElement | null>
	readonly refWrapperListBox: RefObject<HTMLDivElement | null>
	readonly refSelectWrapper: RefObject<HTMLDivElement | null>
	readonly disabled: boolean
	readonly handleBlur?: FocusEventHandler<HTMLDivElement>
	readonly handleFocus?: FocusEventHandler<HTMLDivElement>
	readonly handleHover?: () => void
	readonly hasError: boolean
	readonly errorMessage?: string
	readonly styles: Record<string, string>
	readonly focused: boolean
	readonly isFilled: boolean
	readonly open: boolean
	readonly closeDropdown: () => void
	readonly toggleOpen: () => void
	readonly handleArrowDown: () => void
	readonly dataTestId?: string
	readonly isMobile?: boolean
}

export const WrapperDropdown = ({
	children,
	id,
	dataTestId,
	refDropdown,
	refWrapperListBox,
	disabled,
	handleBlur,
	handleFocus,
	handleHover,
	hasError,
	errorMessage,
	styles,
	focused,
	isFilled,
	open,
	closeDropdown,
	toggleOpen,
	handleArrowDown,
	refSelectWrapper,
	isMobile = false,
}: WrapperDropdownProps): ReactNode => {
	const dropdownProps = {
		id,
		ref: refWrapperListBox,
		'aria-controls': id,
		'aria-expanded': open,
		// 'aria-haspopup': 'listbox',
		'aria-labelledby': `${id}.label`,
		role: 'combobox',
		'data-testid': dataTestId,
		'aria-disabled': disabled,
		onClick: toggleOpen,
		onBlur: (e: FocusEvent<HTMLDivElement>) => {
			if (disabled || !handleBlur) {
				return
			}
			handleBlur(e)
		},
		onFocus: (e: FocusEvent<HTMLDivElement>) => {
			if (disabled || !handleFocus) {
				return
			}
			handleFocus(e)
		},
		onMouseOver: () => {
			if (disabled || !handleHover) {
				return
			}
			handleHover()
		},
		tabIndex: 0,
		onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
			if (disabled) {
				return
			}

			const keyActions: { [key: string]: () => void } = {
				' ': () => toggleOpen(),
				Enter: () => toggleOpen(),
				Escape: () => closeDropdown(),
				ArrowDown: () => handleArrowDown(),
				Tab: () => closeDropdown(),
			}

			keyActions[event.key]?.()
		},
	}

	const className = cx(
		styles.box,
		isFilled && styles.filled,
		focused && styles.focused,
		(errorMessage || hasError) && styles.boxError,
		disabled && styles.disabled,
		open && styles.open
	)

	const refDropdownCb = useCallback(() => {
		if (refDropdown?.current && !disabled) {
			if (open) {
				const firstChild = refDropdown.current.firstElementChild as HTMLElement
				firstChild.focus()
				refDropdown.current.dataset.index = '1'
			} else {
				refDropdown.current.dataset.index = '0'
			}
		}
	}, [])

	useEffect(() => {
		refDropdownCb()
	}, [open])

	useOutsideClick(refSelectWrapper, () => {
		closeDropdown()
	})

	if (isMobile) {
		return <div className={className}>{children}</div>
	}
	return (
		<div className={className} {...dropdownProps}>
			{children}
		</div>
	)
}
